<template>
  <div class="page-wrap">
    <!-- 试卷信息头 -->
    <el-card shadow="never" class="paper-info-card">
      <div class="info-header">
        <div>
          <el-button type="text" icon="el-icon-arrow-left" @click="$router.push('/paper/list')" style="padding:0;margin-right:8px">返回</el-button>
          <span class="paper-name">{{ paper.title }}</span>
          <el-tag :type="paper.status === 1 ? 'success' : 'info'" size="small" style="margin-left:8px">
            {{ paper.status === 1 ? '已发布' : '草稿' }}
          </el-tag>
        </div>
        <div class="info-actions">
          <span class="q-count-label">共 <strong>{{ questions.length }}</strong> 题（满分100分）</span>
          <el-button size="small" icon="el-icon-download" :disabled="questions.length === 0" :loading="exportingStudent" @click="exportPaper('student')">导出学生版</el-button>
          <el-button size="small" icon="el-icon-download" :disabled="questions.length === 0" :loading="exportingTeacher" @click="exportPaper('teacher')">导出教师版</el-button>
          <el-button
            :type="paper.status === 1 ? 'warning' : 'success'"
            size="small"
            @click="toggleStatus"
          >{{ paper.status === 1 ? '下架' : '发布试卷' }}</el-button>
        </div>
      </div>
      <div v-if="paper.description" class="paper-desc">{{ paper.description }}</div>
    </el-card>

    <!-- 题目列表 -->
    <el-card shadow="never" style="margin-top:12px">
      <div slot="header" class="card-header">
        <span>已组题目</span>
        <div class="card-header-actions">
          <span class="source-switch-wrap">
            <span class="source-switch-label">显示来源</span>
            <el-switch v-model="showQuestionSource" />
          </span>
          <el-button size="small" type="primary" icon="el-icon-plus" @click="openAddDialog">从题库添加</el-button>
        </div>
      </div>

      <el-empty v-if="questions.length === 0" description="暂无题目，点击「从题库添加」开始组卷" />

      <div v-for="(q, idx) in questions" :key="q.id" class="q-row">
        <div class="q-row-left">
          <span class="q-index">{{ idx + 1 }}</span>
          <div class="q-info">
            <div class="q-meta-line">
              <DifficultyStars :value="q.difficulty" readonly compact />
              <div v-if="showQuestionSource && formatPaperSource(q)" class="q-source">{{ formatPaperSource(q) }}</div>
            </div>
            <div class="q-content-text rich-html" v-html="renderHtml(q.content)"></div>
            <!-- 选项展示 -->
            <div class="q-options" v-if="parseOptions(q.optionsJson).length">
              <div v-for="(opt, i) in parseOptions(q.optionsJson)" :key="i" class="q-option-item">
                <span class="q-option-key">{{ opt.key }}.</span>
                <div class="q-option-value rich-html" v-html="renderHtml(opt.value)"></div>
              </div>
            </div>
            <div class="q-tags">
              <el-tag size="mini" type="">{{ typeMap[q.type] || q.type }}</el-tag>
              <el-tag v-for="tag in getLeafTagNames(q.knowledgeTags)" :key="'k-' + tag" size="mini" type="success">{{ tag }}</el-tag>
              <el-tag v-for="tag in getLeafTagNames(q.questionTag)" :key="'q-' + tag" size="mini" type="info">{{ tag }}</el-tag>
            </div>
          </div>
        </div>
        <div class="q-row-actions">
          <el-button type="text" icon="el-icon-top" :disabled="idx === 0" @click="moveQuestion(idx, -1)">上移</el-button>
          <el-button type="text" icon="el-icon-bottom" :disabled="idx === questions.length - 1" @click="moveQuestion(idx, 1)">下移</el-button>
          <el-button type="text" icon="el-icon-delete" class="danger-text" @click="removeQuestion(q)">移除</el-button>
        </div>
      </div>
    </el-card>

    <!-- 从题库选题 Dialog -->
    <el-dialog title="从题库添加题目" :visible.sync="addDialogVisible" width="1040px" top="4vh">
      <div class="add-dialog-body">
        <div class="add-main">
          <div class="add-filter">
            <el-select v-model="addFilter.subjectId" placeholder="学科" clearable style="width:100px" @change="onAddFilterChange">
              <el-option v-for="s in subjects" :key="s.id" :label="s.name" :value="s.id" />
            </el-select>
            <el-select v-model="addFilter.stageId" placeholder="学段" clearable style="width:100px" @change="onAddFilterChange">
              <el-option v-for="st in stages" :key="st.id" :label="st.name" :value="st.id" />
            </el-select>
            <el-select v-model="addFilter.gradeId" placeholder="年级" clearable style="width:100px" @change="onAddFilterChange">
              <el-option v-for="g in grades" :key="g.id" :label="g.name" :value="g.id" />
            </el-select>
            <el-cascader
              v-model="addFilter.knowledgeTagPath"
              :options="knowledgeTagOptions"
              :props="{ value: 'value', label: 'label', children: 'children', checkStrictly: true, emitPath: true }"
              placeholder="知识点"
              clearable
              filterable
              style="width:200px"
              @change="onKnowledgeTagChange"
            />
            <el-cascader
              v-model="addFilter.questionTagPath"
              :options="questionTagOptions"
              :props="{ value: 'value', label: 'label', children: 'children', checkStrictly: true, emitPath: true }"
              placeholder="题型标签"
              clearable
              filterable
              style="width:180px"
              @change="onQuestionTagChange"
            />
            <el-select v-model="addFilter.type" placeholder="题型" clearable style="width:100px" @change="onTypeFilterChange">
              <el-option v-for="(label, key) in typeMap" :key="key" :label="label" :value="key" />
            </el-select>
            <el-input v-model="addFilter.keyword" placeholder="搜索题目关键词" clearable style="width:160px"
              @keyup.enter.native="onAddFilterChange" @clear="onAddFilterChange" />
          </div>
          <div v-loading="addLoading" class="add-question-list">
            <el-empty v-if="!addLoading && addQuestions.length === 0" description="暂无题目" />
            <div
              v-for="q in addQuestions" :key="q.id"
              class="add-q-row"
              :class="{ selected: isInBasket(q.id), added: addedIds.includes(q.id) }"
              @click="!addedIds.includes(q.id) && toggleSelect(q)"
            >
              <div class="add-q-check">
                <i v-if="addedIds.includes(q.id)" class="el-icon-check" style="color:#52c41a"></i>
                <el-checkbox v-else :value="isInBasket(q.id)" @change="toggleSelect(q)" @click.native.stop />
              </div>
              <div class="add-q-content">
                <div class="add-q-text rich-html" v-html="renderHtml(q.content)"></div>
                <div class="add-q-options" v-if="parseOptions(q.optionsJson).length">
                  <div v-for="(opt, i) in parseOptions(q.optionsJson)" :key="i" class="add-q-option-item">
                    <span class="add-q-option-key">{{ opt.key }}.</span>
                    <div class="add-q-option-value rich-html" v-html="renderHtml(opt.value)"></div>
                  </div>
                </div>
                <div class="add-q-tags">
                  <el-tag size="mini">{{ typeMap[q.type] || q.type }}</el-tag>
                  <DifficultyStars :value="q.difficulty" readonly compact />
                  <span v-if="q.source" class="add-q-source">{{ q.source }}</span>
                  <el-tag v-for="tag in getLeafTagNames(q.knowledgeTags)" :key="'k-' + tag" size="mini" type="success">{{ tag }}</el-tag>
                  <el-tag v-for="tag in getLeafTagNames(q.questionTag)" :key="'q-' + tag" size="mini" type="info">{{ tag }}</el-tag>
                </div>
              </div>
              <el-button v-if="!addedIds.includes(q.id)" type="text" size="mini" @click.stop="addSingle(q.id)">加入</el-button>
              <span v-else style="font-size:12px;color:#52c41a;padding-right:8px">已加入</span>
            </div>
          </div>
          <el-pagination
            style="text-align:right;margin-top:8px"
            @current-change="handleAddPageChange"
            :current-page="addPage + 1"
            :page-size="addSize"
            :total="addTotal"
            layout="total, prev, pager, next"
            small
          />
        </div>
        <div class="add-basket-panel">
          <div class="basket-header">
            <span class="basket-title">试题篮（{{ basketItems.length }}）</span>
            <el-button type="text" size="mini" :disabled="!basketItems.length" @click="clearBasket">清空</el-button>
          </div>
          <div class="basket-list">
            <el-empty v-if="!basketItems.length" description="勾选题目加入试题篮，可跨页累计" :image-size="56" />
            <div v-for="(item, idx) in basketItems" :key="item.id" class="basket-item">
              <span class="basket-index">{{ idx + 1 }}</span>
              <div class="basket-item-body">
                <div class="basket-item-text rich-html" v-html="renderHtml(item.content)"></div>
                <div v-if="item.knowledgeTags" class="basket-item-tags">
                  <el-tag v-for="tag in getLeafTagNames(item.knowledgeTags)" :key="tag" size="mini" type="success">{{ tag }}</el-tag>
                </div>
              </div>
              <el-button type="text" icon="el-icon-close" class="basket-remove" @click="removeFromBasket(item.id)" />
            </div>
          </div>
          <el-button type="primary" style="width:100%;margin-top:8px" :disabled="!basketItems.length" @click="batchAdd">
            加入试卷（{{ basketItems.length }}）
          </el-button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import request from '../../utils/request'
import filterLoader from '../../mixins/filterLoader'
import { renderHtml } from '../../utils/renderHtml'
import { parseQuestionOptions } from '../../utils/questionOptions'
import DifficultyStars from '../../components/DifficultyStars.vue'
import { getLeafTagNames } from '../../utils/tagDisplay'

export default {
  components: { DifficultyStars },
  name: 'PaperDetail',
  mixins: [filterLoader],
  data() {
    return {
      paper: {},
      questions: [],
      grades: [],
      subjects: [],
      stages: [],
      addDialogVisible: false,
      addLoading: false,
      addFilter: {
        gradeId: null, subjectId: null, stageId: null, keyword: '', type: null,
        knowledgeTagPath: [], knowledgeTagNodeId: null,
        questionTagPath: [], questionTagNodeId: null
      },
      addQuestions: [],
      addPage: 0,
      addSize: 10,
      addTotal: 0,
      basketItems: [],
      addedIds: [],
      knowledgeTagOptions: [],
      questionTagOptions: [],
      orderSaving: false,
      exportingStudent: false,
      exportingTeacher: false,
      showQuestionSource: true,
      typeMap: { SINGLE: '单选题', MULTI: '多选题', FILL: '填空题', QA: '问答题' }
    }
  },
  computed: {
    paperId() { return this.$route.params.id }
  },
  created() {
    this.loadPaper()
    this.loadGradeAndSubject()
    this.loadStages()
  },
  methods: {
    renderHtml,
    parseOptions: parseQuestionOptions,
    // loadFilters 由 mixin 的 loadGradeAndSubject 替代
    loadStages() {
      request.get('/stage').then(data => {
        this.stages = data || []
      })
    },
    loadPaper() {
      // 加载试卷基本信息和题目
      request.get(`/paper/${this.paperId}/questions`).then(qs => {
        this.questions = qs || []
      })
      // 从列表接口取基本信息（简单方案：直接从管理接口）
      request.get('/paper/list', { params: { page: 0, size: 100 } }).then(data => {
        const found = (data.content || []).find(p => String(p.id) === String(this.paperId))
        if (found) this.paper = found
      })
    },
    toggleStatus() {
      const newStatus = this.paper.status === 1 ? 0 : 1
      const label = newStatus === 1 ? '发布' : '下架'
      this.$confirm(`确认${label}该试卷？`, '提示', { type: 'warning' }).then(() => {
        request.put(`/paper/${this.paperId}/status`, { status: newStatus }).then(() => {
          this.$message.success(`已${label}`)
          this.paper = { ...this.paper, status: newStatus }
        })
      }).catch(() => {})
    },
    removeQuestion(q) {
      this.$confirm(`确认从试卷中移除该题目？`, '提示', { type: 'warning' }).then(() => {
        request.delete(`/paper/${this.paperId}/questions/${q.id}`).then(() => {
          this.$message.success('已移除')
          this.questions = this.questions.filter(x => x.id !== q.id)
        })
      }).catch(() => {})
    },
    moveQuestion(index, step) {
      const target = index + step
      if (target < 0 || target >= this.questions.length || this.orderSaving) return
      const list = [...this.questions]
      const temp = list[index]
      list[index] = list[target]
      list[target] = temp
      this.questions = list
      this.saveQuestionOrder()
    },
    saveQuestionOrder() {
      const questionIds = this.questions.map(q => q.id)
      this.orderSaving = true
      request.put(`/paper/${this.paperId}/questions/order`, { questionIds }).then(() => {
        this.$message.success('顺序已更新')
      }).catch(e => {
        this.$message.error(e.message || '调整顺序失败')
        this.loadPaper()
      }).finally(() => { this.orderSaving = false })
    },
    formatPaperSource(q) {
      const source = q && q.source ? String(q.source).trim() : ''
      return source ? `来源：${source}` : ''
    },
    exportPaper(version) {
      const loadingKey = version === 'teacher' ? 'exportingTeacher' : 'exportingStudent'
      this[loadingKey] = true
      const token = localStorage.getItem('admin_token')
      const showSource = this.showQuestionSource ? 'true' : 'false'
      fetch(`/api/admin/paper/${this.paperId}/export-pdf?version=${version}&showSource=${showSource}`, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/pdf' }
      }).then(async res => {
        const contentType = res.headers.get('content-type') || ''
        if (contentType.includes('application/json')) {
          const err = await res.json()
          this.$message.error(err.message || '导出失败')
          return
        }
        if (!res.ok) {
          this.$message.error('导出失败，请稍后重试')
          return
        }
        const blob = await res.blob()
        if (!blob.size) {
          this.$message.error('导出文件为空')
          return
        }
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        const versionLabel = version === 'teacher' ? '教师版' : '学生版'
        a.download = `${this.paper.title || '试卷'}_${versionLabel}.pdf`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        this.$message.success('导出成功')
      }).catch(() => {
        this.$message.error('导出失败')
      }).finally(() => { this[loadingKey] = false })
    },
    openAddDialog() {
      this.addDialogVisible = true
      this.basketItems = []
      this.addedIds = []
      this.addFilter = {
        gradeId: this.paper.gradeId || null,
        subjectId: this.paper.subjectId || null,
        stageId: this.paper.stageId || null,
        keyword: '',
        type: null,
        knowledgeTagPath: [],
        knowledgeTagNodeId: null,
        questionTagPath: [],
        questionTagNodeId: null
      }
      this.syncStageFromGrade()
      this.addPage = 0
      this.loadTagTrees()
      this.loadAddQuestions()
    },
    syncStageFromGrade() {
      if (this.addFilter.gradeId) {
        const g = this.grades.find(x => x.id === this.addFilter.gradeId)
        if (g && g.stageId) {
          this.addFilter.stageId = g.stageId
        }
      }
    },
    loadTagTrees() {
      this.loadKnowledgeTagTree()
      this.loadQuestionTagTree()
    },
    loadKnowledgeTagTree() {
      const subjectId = this.addFilter.subjectId || this.paper.subjectId
      const stageId = this.addFilter.stageId || this.paper.stageId
      const params = { tagType: 'knowledge' }
      if (subjectId) params.subjectId = subjectId
      if (stageId) params.stageId = stageId
      request.get('/tag-tree', { params }).then(tree => {
        this.knowledgeTagOptions = this.buildCascaderOptions(tree || [])
      }).catch(() => { this.knowledgeTagOptions = [] })
    },
    loadQuestionTagTree() {
      const subjectId = this.addFilter.subjectId || this.paper.subjectId
      const stageId = this.addFilter.stageId || this.paper.stageId
      const params = { tagType: 'question' }
      if (subjectId) params.subjectId = subjectId
      if (stageId) params.stageId = stageId
      request.get('/tag-tree', { params }).then(tree => {
        this.questionTagOptions = this.buildCascaderOptions(tree || [])
      }).catch(() => { this.questionTagOptions = [] })
    },
    buildCascaderOptions(nodes) {
      if (!nodes || !nodes.length) return []
      return nodes.map(n => ({
        value: n.id,
        label: n.name,
        children: n.children && n.children.length ? this.buildCascaderOptions(n.children) : undefined
      }))
    },
    getLeafTagNames,
    onKnowledgeTagChange(path) {
      this.addFilter.knowledgeTagNodeId = path && path.length ? path[path.length - 1] : null
      this.addPage = 0
      this.loadAddQuestions()
    },
    onQuestionTagChange(path) {
      this.addFilter.questionTagNodeId = path && path.length ? path[path.length - 1] : null
      this.addPage = 0
      this.loadAddQuestions()
    },
    onTypeFilterChange() {
      this.addPage = 0
      this.loadAddQuestions()
    },
    loadAddQuestions() {
      this.addLoading = true
      const { gradeId, subjectId, keyword, type, knowledgeTagNodeId, questionTagNodeId } = this.addFilter
      const stageId = this.addFilter.stageId || this.paper.stageId || null
      request.get('/question/list', {
        params: {
          page: this.addPage, size: this.addSize, gradeId, subjectId, stageId, keyword, type,
          knowledgeTagNodeId, questionTagNodeId, status: 1
        }
      }).then(data => {
        this.addQuestions = data.content || []
        this.addTotal = data.totalElements || 0
      }).finally(() => { this.addLoading = false })
    },
    onAddFilterChange() {
      this.addFilter.knowledgeTagPath = []
      this.addFilter.knowledgeTagNodeId = null
      this.addFilter.questionTagPath = []
      this.addFilter.questionTagNodeId = null
      this.addPage = 0
      this.syncStageFromGrade()
      this.loadTagTrees()
      this.loadAddQuestions()
    },
    handleAddPageChange(val) { this.addPage = val - 1; this.loadAddQuestions() },
    isInBasket(id) {
      return this.basketItems.some(i => i.id === id)
    },
    toggleSelect(q) {
      if (this.addedIds.includes(q.id)) return
      const idx = this.basketItems.findIndex(i => i.id === q.id)
      if (idx > -1) {
        this.basketItems.splice(idx, 1)
      } else {
        this.basketItems.push({
          id: q.id,
          content: q.content,
          knowledgeTags: q.knowledgeTags,
          questionTag: q.questionTag,
          type: q.type
        })
      }
    },
    removeFromBasket(id) {
      this.basketItems = this.basketItems.filter(i => i.id !== id)
    },
    clearBasket() {
      this.basketItems = []
    },
    addSingle(questionId) {
      request.post(`/paper/${this.paperId}/questions`, { questionId }).then(() => {
        this.$message.success('已加入试卷')
        this.addedIds.push(questionId)
        this.removeFromBasket(questionId)
        request.get(`/paper/${this.paperId}/questions`).then(qs => { this.questions = qs || [] })
      }).catch(e => {
        this.$message.error(e.message || '操作失败')
      })
    },
    batchAdd() {
      const questionIds = this.basketItems.map(i => i.id).filter(id => !this.addedIds.includes(id))
      if (questionIds.length === 0) return
      request.post(`/paper/${this.paperId}/questions/batch`, { questionIds }).then(data => {
        const added = data.added || 0
        this.$message.success(`已加入 ${added} 题（重复的自动跳过）`)
        this.addedIds = [...this.addedIds, ...questionIds]
        this.basketItems = this.basketItems.filter(i => !questionIds.includes(i.id))
        request.get(`/paper/${this.paperId}/questions`).then(qs => { this.questions = qs || [] })
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.paper-info-card { margin-bottom: 0; }
.info-header { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px; }
.paper-name { font-size: 16px; font-weight: 700; color: #222; }
.info-actions { display: flex; align-items: center; gap: 12px; }
.q-count-label { font-size: 13px; color: #666; }
.paper-desc { font-size: 13px; color: #888; margin-top: 8px; }
.card-header { display: flex; align-items: center; justify-content: space-between; }
.card-header-actions { display: flex; align-items: center; gap: 12px; }
.source-switch-wrap { display: inline-flex; align-items: center; gap: 6px; }
.source-switch-label { font-size: 13px; color: #666; }

/* 已组题目行 */
.q-row {
  display: flex; align-items: flex-start; justify-content: space-between;
  padding: 12px 0; border-bottom: 1px solid #f5f5f5;
}
.q-row-actions { display: flex; flex-direction: column; align-items: flex-end; flex-shrink: 0; gap: 0; }
.q-row-actions .el-button { padding: 4px 0; margin: 0; }
.q-row:last-child { border-bottom: none; }
.q-row-left { display: flex; gap: 12px; flex: 1; }
.q-index {
  width: 26px; height: 26px; border-radius: 50%; background: #1677ff; color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; flex-shrink: 0;
}
.q-info { flex: 1; }
.q-meta-line { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-bottom: 6px; }
.q-content-text { font-size: 14px; color: #333; line-height: 1.6; margin-bottom: 6px; }
.q-source { font-size: 12px; color: #d48806; margin-bottom: 0; }
.add-q-source { font-size: 12px; color: #d48806; margin-left: 6px; }
.q-tags { display: flex; gap: 4px; flex-wrap: wrap; align-items: center; margin-top: 6px; }
.tag-small { font-size: 12px; color: #999; }

/* 已组题目选项样式 */
.q-options {
  margin: 8px 0;
  padding: 8px 12px;
  background: #fafafa;
  border-radius: 4px;
}
.q-option-item {
  display: flex;
  align-items: flex-start;
  padding: 3px 0;
  font-size: 13px;
  color: #555;
  line-height: 1.5;
}
.q-option-key {
  font-weight: 600;
  color: #333;
  min-width: 20px;
  margin-right: 4px;
}
.q-option-value {
  flex: 1;
}
.q-option-value img {
  max-width: 100%;
  max-height: 60px;
  vertical-align: middle;
}

/* 添加题目弹框 */
.add-filter { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; margin-bottom: 12px; }
.add-dialog-body { display: flex; gap: 12px; align-items: stretch; min-height: 460px; }
.add-main { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.add-basket-panel {
  width: 268px; flex-shrink: 0; border-left: 1px solid #eee; padding-left: 12px;
  display: flex; flex-direction: column;
}
.basket-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.basket-title { font-size: 14px; font-weight: 600; color: #333; }
.basket-list { flex: 1; overflow-y: auto; max-height: 400px; min-height: 120px; }
.basket-item {
  display: flex; gap: 6px; align-items: flex-start; padding: 8px 4px;
  border-bottom: 1px solid #f5f5f5; font-size: 12px;
}
.basket-index { color: #1677ff; font-weight: 600; flex-shrink: 0; width: 18px; }
.basket-item-body { flex: 1; min-width: 0; }
.basket-item-text {
  line-height: 1.4; color: #444;
  display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 3; overflow: hidden;
}
.basket-item-tags { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 4px; }
.basket-remove { padding: 0; color: #999; flex-shrink: 0; }
.add-question-list { max-height: 400px; overflow-y: auto; flex: 1; }
.add-q-row {
  display: flex; align-items: flex-start; gap: 8px; padding: 10px 8px;
  border-radius: 4px; cursor: pointer; transition: background 0.15s;
  border-bottom: 1px solid #f5f5f5;
}
.add-q-row:hover { background: #f0f5ff; }
.add-q-row.selected { background: #e6f4ff; }
.add-q-row.added { opacity: 0.6; cursor: default; }
.add-q-check { flex-shrink: 0; padding-top: 2px; width: 20px; }
.add-q-content { flex: 1; }
.add-q-text { font-size: 14px; color: #333; margin-bottom: 4px; line-height: 1.5;
  display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; overflow: hidden; }
.add-q-options {
  margin-bottom: 6px;
  padding: 6px 10px;
  background: #fafafa;
  border-radius: 4px;
}
.add-q-option-item {
  display: flex;
  align-items: flex-start;
  padding: 2px 0;
  font-size: 12px;
  color: #555;
  line-height: 1.4;
}
.add-q-option-key {
  font-weight: 600;
  color: #333;
  min-width: 18px;
  margin-right: 2px;
}
.add-q-option-value {
  flex: 1;
}
.add-q-option-value img {
  max-width: 100%;
  max-height: 40px;
  vertical-align: middle;
}
.add-q-tags { display: flex; gap: 4px; flex-wrap: wrap; margin-top: 4px; }
.danger-text { color: #ff4d4f; }
</style>
